import { createRoute } from '@tanstack/react-router';

import { queryClient, queryKeys } from '@web/core/query';
import { projectsRepository } from '@web/data/repositories';
import {
  ProjectEditorLayout,
  ProjectIdentityPage,
  ProjectSettingsPage,
  ProjectSquadsPage,
  ProjectUsersPage,
} from '@web/ui/projects/views';
import type { Permission } from '@voll-intelligence/voll-authz';
import { ProjectIdSchema } from '@voll-intelligence/domain';

import { orgWorkspaceLayoutRoute } from './overview_routes';
import {
  redirectToOrgHome,
  requireOrgPermission,
  resolveOrgFromSlug,
} from './org_permission_guard';

async function requireProjectPermission(
  orgSlug: string,
  projectIdParam: string,
  perm: Permission,
): Promise<void> {
  // Validate the URL param against the branded schema before using it in a
  // cache key or request URL. A bad shape from a hand-crafted URL must not
  // reach the network or pollute the query cache.
  const parsedProjectId = ProjectIdSchema.safeParse(projectIdParam);
  if (!parsedProjectId.success) {
    redirectToOrgHome(orgSlug);
  }
  const projectId = parsedProjectId.data;
  const { orgId } = await resolveOrgFromSlug(orgSlug);
  let project;
  try {
    project = await queryClient.ensureQueryData({
      queryKey: queryKeys.projects.detail(orgId, projectId),
      queryFn: () => projectsRepository.getProject(orgId, projectId),
    });
  } catch {
    redirectToOrgHome(orgSlug);
  }
  if (!project.permissions.includes(perm)) {
    redirectToOrgHome(orgSlug);
  }
}

export const projectNewLayoutRoute = createRoute({
  getParentRoute: () => orgWorkspaceLayoutRoute,
  path: 'project/new',
  component: ProjectEditorLayout,
  beforeLoad: ({ params }) => requireOrgPermission(params.orgSlug, 'kanban:projects:create'),
});

export const projectNewIdentityRoute = createRoute({
  getParentRoute: () => projectNewLayoutRoute,
  path: '/',
  component: ProjectIdentityPage,
});

export const projectEditLayoutRoute = createRoute({
  getParentRoute: () => orgWorkspaceLayoutRoute,
  path: 'project/$projectId',
  component: ProjectEditorLayout,
  beforeLoad: ({ params }) =>
    requireProjectPermission(params.orgSlug, params.projectId, 'kanban:projects:read'),
});

// Identity is the index of the project editor — `/project/$projectId`
// renders the identity page directly, no redirect.
export const projectIdentityRoute = createRoute({
  getParentRoute: () => projectEditLayoutRoute,
  path: '/',
  component: ProjectIdentityPage,
});

export const projectSquadsRoute = createRoute({
  getParentRoute: () => projectEditLayoutRoute,
  path: 'squads',
  component: ProjectSquadsPage,
});

export const projectUsersRoute = createRoute({
  getParentRoute: () => projectEditLayoutRoute,
  path: 'users',
  component: ProjectUsersPage,
  beforeLoad: ({ params }) =>
    requireProjectPermission(params.orgSlug, params.projectId, 'kanban:project-members:read'),
});

export const projectSettingsRoute = createRoute({
  getParentRoute: () => projectEditLayoutRoute,
  path: 'settings',
  component: ProjectSettingsPage,
  beforeLoad: ({ params }) =>
    requireProjectPermission(params.orgSlug, params.projectId, 'kanban:projects:update'),
});
